import { isAfter, subDays } from 'date-fns';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { ONE_WEEK_IN_DAYS } from '@/constants/time';
import { getPlatformStorage, safeStorageOp } from '@/util/platformStorage';

export type SurveyVisits = Record<string, string>;

export type SurveyAcknowledgements = Record<string, string>;

const visitsStorageKey = (userId: string) => `${STORAGE_KEYS.SURVEY_VISITED}:${userId}`;

const acknowledgementsStorageKey = (userId: string) =>
  `${STORAGE_KEYS.SURVEY_ACKNOWLEDGED}:${userId}`;

/**
 * Whether a visit is recent enough to still offer the survey back. A visit
 * walked past for a week has been declined in practice.
 */
export function isVisitUnexpired(visitedAt: string): boolean {
  return isAfter(new Date(visitedAt), subDays(new Date(), ONE_WEEK_IN_DAYS));
}

async function readTimestampMap(
  userId: string,
  storageKey: string
): Promise<Record<string, string>> {
  const raw = await safeStorageOp(() => getPlatformStorage(userId, 'local').getItem(storageKey));

  if (!raw) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    return parsed as Record<string, string>;
  } catch {
    return {};
  }
}

async function writeTimestampMap(
  userId: string,
  storageKey: string,
  entries: Record<string, string>
): Promise<void> {
  await safeStorageOp(() =>
    getPlatformStorage(userId, 'local').setItem(storageKey, JSON.stringify(entries))
  );
}

function withoutExpired(entries: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(entries).filter(([, at]) => isVisitUnexpired(at)));
}

/**
 * Reads the user's survey visits, keyed by survey group id (or slug) with an
 * ISO timestamp of the first visit as the value.
 *
 * Visits live in one JSON map per user rather than a key per survey, because
 * the resume UI has to enumerate every visited survey and the platform
 * storage interface exposes no key listing.
 *
 * Known limitation: visits are device-local. Whether a survey was answered is
 * server-side, but whether it was opened is not, so a survey abandoned on
 * mobile will not offer itself back on web.
 */
export async function readSurveyVisits(userId: string): Promise<SurveyVisits> {
  return readTimestampMap(userId, visitsStorageKey(userId));
}

/**
 * Records that the user opened a survey, keeping the original timestamp if
 * they open it again so the value stays a first-seen marker, and dropping
 * entries that have aged out so the map cannot grow without a ceiling.
 *
 * Recording a first visit also clears any acknowledgement left over for that
 * survey, so a fresh offer arrives un-silenced. Returns whether a visit was
 * newly recorded, which is what tells callers the nudge just re-armed.
 */
export async function markSurveyVisited(userId: string, surveyGroupId: string): Promise<boolean> {
  const visits = await readSurveyVisits(userId);

  if (visits[surveyGroupId]) {
    return false;
  }

  const next: SurveyVisits = {
    ...withoutExpired(visits),
    [surveyGroupId]: new Date().toISOString(),
  };

  await writeTimestampMap(userId, visitsStorageKey(userId), next);

  await clearSurveyAcknowledgement(userId, surveyGroupId);

  return true;
}

/**
 * Reads the surveys whose badge the user has already answered, in the same
 * shape as the visits map.
 *
 * Kept in its own map rather than folded into the visit value so the two
 * questions stay separable: the visit is what makes a survey resumable at all,
 * and the acknowledgement only silences the nudge pointing at it.
 */
export async function readSurveyAcknowledgements(userId: string): Promise<SurveyAcknowledgements> {
  return readTimestampMap(userId, acknowledgementsStorageKey(userId));
}

/**
 * Records that the user has taken the badge up on its offer for this survey,
 * which retires the badge while leaving the survey resumable.
 *
 * Ages out on the same window as a visit, so an acknowledgement cannot outlive
 * the offer it silenced and leave a later visit permanently unbadged.
 */
export async function markSurveyAcknowledged(userId: string, surveyGroupId: string): Promise<void> {
  const acknowledgements = await readSurveyAcknowledgements(userId);

  const next: SurveyAcknowledgements = {
    ...withoutExpired(acknowledgements),
    [surveyGroupId]: new Date().toISOString(),
  };

  await writeTimestampMap(userId, acknowledgementsStorageKey(userId), next);
}

/**
 * Drops one survey's acknowledgement, letting its badge speak again.
 */
export async function clearSurveyAcknowledgement(
  userId: string,
  surveyGroupId: string
): Promise<void> {
  const acknowledgements = await readSurveyAcknowledgements(userId);

  if (!acknowledgements[surveyGroupId]) {
    return;
  }

  const next = withoutExpired(acknowledgements);

  delete next[surveyGroupId];

  await writeTimestampMap(userId, acknowledgementsStorageKey(userId), next);
}
